import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdAdd } from 'react-icons/io';
import { toast } from 'react-toastify';
import SelectFilter from '../../organisms/SelectFilter';
import { configService } from '../../../services/configService';

interface PLDRule {
    id: string;
    name: string;
    provider: string;
    context: string;
    field_path: string;
    field_type: string;
    comparison_type: string;
    comparison_value: string;
    recommendation: string;
    pld_flag: boolean;
    credit_flag: boolean;
    active: boolean;
    created_by: string;
    created_at: string;
    updated_at: string;
}

const RecommendationEngineRulesList: React.FC = () => {
    const navigate = useNavigate();

    const [provider, setProvider] = useState('');
    const [context, setContext] = useState('');
    const [active, setActive] = useState('');
    const [rules, setRules] = useState<PLDRule[]>([]);
    const [loading, setLoading] = useState(false);

    // Obter configuração da API
    const apiConfig = configService.getConfig();

    // Construir URL com filtros
    const buildUrl = () => {
        let url = `${apiConfig.apiUrl}${apiConfig.endpoints.rules}`;
        const params = [];

        // Adicionar filtros específicos se selecionados
        if (provider) params.push(`provider=${provider}`);
        if (context) params.push(`context=${context}`);
        if (active) params.push(`active=${active}`);

        return url + (params.length > 0 ? '?' + params.join('&') : '');
    };

    // Carregar regras da API
    const loadRules = async () => {
        setLoading(true);
        try {
            // Pegar token do localStorage ou Auth
            const authToken = localStorage.getItem('authToken');
            
            const response = await fetch(buildUrl(), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    [apiConfig.authentication.headerName]: apiConfig.authentication.apiKey,
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setRules(data.rules || []);
            } else {
                console.error('Erro ao carregar regras:', response.statusText);
                toast.error('Erro ao carregar regras da API');
                setRules([]);
            }
        } catch (error) {
            console.error('Erro ao carregar regras:', error);
            toast.error('Erro de conexão ao carregar regras');
            setRules([]);
        } finally {
            setLoading(false);
        }
    };

    // Carregar regras na inicialização e quando filtros mudarem
    useEffect(() => {
        loadRules();
    }, [provider, context, active]);


    const contextLabel: Record<string, string> = {
        PLD: 'PLD',
        CREDIT: 'CRÉDITO',
    };

    const formatContext = (ctx?: string) => contextLabel[ctx ?? ''] ?? (ctx ?? '-');


    const renderRows = (rows: PLDRule[]) => {
        if (!rows || rows.length === 0) {
            return (
                <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        {loading ? 'Carregando regras...' : 'Nenhuma regra encontrada'}
                    </td>
                </tr>
            );
        }

        return rows.map((rule) => (
            <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                    <div className="font-medium">{rule.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{rule.field_path}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${rule.provider === 'INOVAMIND' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        rule.provider === 'ESCAVADOR' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            rule.provider === 'QUOD' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}>
                        {rule.provider}
                    </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${rule.context === 'PLD' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                        {formatContext(rule.context)}
                    </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        !rule.recommendation || rule.recommendation === '' 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : rule.recommendation === 'REJECTED' 
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                                : rule.recommendation === 'PENDING' 
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                                    : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                        {!rule.recommendation || rule.recommendation === '' 
                            ? 'Sinalizado Crédito'
                            : rule.recommendation === 'REJECTED' 
                                ? 'Reprovado' 
                                : rule.recommendation === 'PENDING' 
                                    ? 'Pendente' 
                                    : 'Aprovado'}
                    </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${rule.active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}>
                        {rule.active ? 'Ativo' : 'Inativo'}
                    </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                        onClick={() => navigate(`/recommendation-engine/rule/view/${rule.id}`)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 mr-4"
                    >
                        Visualizar
                    </button>
                    <button
                        onClick={() => navigate(`/recommendation-engine/rule/edit/${rule.id}`)}
                        className="text-green-600 hover:text-green-800 dark:text-green-400"
                    >
                        Editar
                    </button>
                </td>
            </tr>
        ));
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            <div className="p-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Regras
                    </h1>
                    <nav className="text-sm text-gray-500 dark:text-gray-400">
                        Motor de Recomendação › Regras
                    </nav>
                </div>

                <div className="mb-4 flex gap-4">
                    <button
                        onClick={() => navigate('/recommendation-engine/rule/form')}
                        className="px-10 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                                flex items-center gap-2 whitespace-nowrap"
                    >
                        <IoMdAdd size={20} />
                        <span>Nova Regra</span>
                    </button>

                    <SelectFilter
                        id='select-provider'
                        label="Provider"
                        value={provider}
                        onChange={setProvider}
                        options={[
                            { value: '', label: 'Todos os Providers' },
                            { value: 'INOVAMIND', label: 'Inovamind' },
                            { value: 'ESCAVADOR', label: 'Escavador' },
                            { value: 'QUOD', label: 'QUOD' },
                            { value: 'SERASA', label: 'Serasa' },
                            { value: 'SCR', label: 'SCR' }
                        ]}
                        className="w-full"
                    />

                    <SelectFilter
                        id='select-context'
                        label="Contexto"
                        value={context}
                        onChange={setContext}
                        options={[
                            { value: '', label: 'Todos os Contextos' },
                            { value: 'PLD', label: 'PLD' },
                            { value: 'CREDIT', label: 'Crédito' }
                        ]}
                        className="w-full"
                    />

                    <SelectFilter
                        id='select-status'
                        label="Status"
                        value={active}
                        onChange={setActive}
                        options={[
                            { value: '', label: 'Todas' },
                            { value: 'true', label: 'Ativas' },
                            { value: 'false', label: 'Inativas' }
                        ]}
                        className="w-full"
                    />
                </div>

                <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando regras...</p>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Nome da Regra
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Provider
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Contexto
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Recomendação
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-slate-700 divide-y divide-gray-200 dark:divide-slate-600">
                                {renderRows(rules)}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecommendationEngineRulesList;
